import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { BillingInfoFieldName } from '@shipstation/financial-services-ui-components';
import type { BillingInfoData } from '@shipstation/financial-services-ui-components';

import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { Text, TextType } from 'ui-components/components/text';

import { US } from '../../constants/country-codes';
import { getBillingInfoFromPaymentMethod } from '../../financial-services-ui/mapping';
import Global from '../../store/global-selectors';
import { useYupFormValidation } from '../../validation/use-yup-form-validation';
import { getErrorMessage } from '../../validation/utilities';
import type { ValidationError } from '../../validation/validation-error';
import { usePaymentAndSubscriptionMegaContext } from '../../pages/settings/payment-and-subscription/context';

import { UserInfoForm } from './components/user-info-form';
import { BillingAddressForm } from './components/billing-address-form';
import { AuctanePayAccountSelect, NEW_PAYMENT_METHOD_VALUE } from './components/auctane-pay-account-select';
import { AutoPayAcceptanceSection } from './components/auto-pay-acceptance-section';
import { TermsSection } from './components/terms-section';
import { PaymentMethodSection } from './components/payment-method-section';

import type { WwexFormData, Address } from './types';
import { buildWwexFormSchema, PAYMENT_METHOD_ID_FIELD, AUTO_PAY_ACCEPTED_FIELD, TERMS_ACCEPTED_FIELD } from './wwex-form-validation-schema';
import { messages } from './messages';

type WwexRegistrationFormProps = {
  formData: Partial<WwexFormData>;
  fsuiBillingInfo: BillingInfoData | undefined;
  isDisabled: boolean;
  showAllErrors: boolean;
  onChange: (data: Partial<WwexFormData>) => void;
  onValidationChange: (isValid: boolean) => void;
  onPaymentSubmitFunctionReady?: (submitFn: (() => void) | null) => void;
  onPaymentMethodCreated?: (paymentMethodId: string, updatedFormData: Partial<WwexFormData>, billingInfo?: BillingInfoData) => void;
};

export const WwexRegistrationForm = ({
  formData,
  fsuiBillingInfo,
  isDisabled,
  showAllErrors,
  onChange,
  onValidationChange,
  onPaymentSubmitFunctionReady,
  onPaymentMethodCreated,
}: WwexRegistrationFormProps) => {
  const sellerCountry = useSelector(Global.selectSellerHomeCountry);
  const { paymentMethodDetails } = usePaymentAndSubscriptionMegaContext();

  // Track touched fields for validation
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Track if user is adding new payment method
  const [isNewPaymentMethodSelected, setIsNewPaymentMethodSelected] = useState(false);

  // Always hold the latest formData so stale Adyen callbacks read current state
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // When creating a new payment method, FSUI already shows billing address
  // For existing payment methods, we show our billing address form (data goes to WWEX, not payment method update)
  const isExistingPaymentMethodSelected = !!formData.paymentMethodId && !isNewPaymentMethodSelected;
  const shouldShowBillingAddress = isExistingPaymentMethodSelected;

  const schema = useMemo(
    () => buildWwexFormSchema(sellerCountry ?? US, shouldShowBillingAddress),
    [sellerCountry, shouldShowBillingAddress],
  );

  const { errors, isValid } = useYupFormValidation({ form: formData, schema });

  // Notify parent of validation changes
  useEffect(() => {
    onValidationChange(isValid);
  }, [isValid, onValidationChange]);

  // ===================
  // User Info Handlers
  // ===================

  const handleUserInfoChange = useCallback(
    (field: string, value: string) => {
      if (isDisabled) return;
      onChange({
        ...formData,
        [field]: value,
      });
    },
    [formData, onChange, isDisabled],
  );

  const handleUserInfoBlur = useCallback((field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // ===================
  // Billing Address Handlers
  // ===================

  const handleBillingAddressChange = useCallback(
    (field: string, value: string | Address) => {
      if (isDisabled) return;
      if (field === 'address') {
        onChange({
          ...formData,
          billingAddress: value as Address,
        });
      } else {
        const billingField = `billing${field.charAt(0).toUpperCase()}${field.slice(1)}`;
        onChange({
          ...formData,
          [billingField]: value,
        });
      }
    },
    [formData, onChange, isDisabled],
  );

  const handleBillingAddressBlur = useCallback((field: string) => {
    if (field.startsWith('address.')) {
      setTouched((prev) => ({ ...prev, [`billingAddress.${field.substring(8)}`]: true }));
    } else {
      const billingField = `billing${field.charAt(0).toUpperCase()}${field.slice(1)}`;
      setTouched((prev) => ({ ...prev, [billingField]: true }));
    }
  }, []);

  // ===================
  // Payment Method Handlers
  // ===================

  const handlePaymentMethodChange = useCallback(
    (paymentMethodId: string) => {
      if (isDisabled) return;
      const isNewMethod = paymentMethodId === NEW_PAYMENT_METHOD_VALUE;
      setIsNewPaymentMethodSelected(isNewMethod);

      // If existing payment method selected, populate billing address from it
      if (!isNewMethod && paymentMethodId) {
        const selectedMethod = paymentMethodDetails.find((method) => method.id === paymentMethodId);
        if (selectedMethod?.accountHolderAddress) {
          const billingInfo = getBillingInfoFromPaymentMethod(selectedMethod, {
            name: formData.firstName && formData.lastName ? `${formData.firstName} ${formData.lastName}` : '',
            homeCountry: sellerCountry ?? US,
            phone: formData.phoneNumber ?? '',
            companyName: formData.companyName ?? '',
          });

          if (billingInfo) {
            const billingAddress = {
              line1: billingInfo[BillingInfoFieldName.Line1] || '',
              line2: billingInfo[BillingInfoFieldName.Line2] || '',
              line3: '',
              city: billingInfo[BillingInfoFieldName.City] || '',
              state: billingInfo[BillingInfoFieldName.State] || '',
              postalCode: billingInfo[BillingInfoFieldName.PostalCode] || '',
              countryCode: billingInfo[BillingInfoFieldName.CountryCode] || 'US',
            };

            onChange({
              ...formData,
              paymentMethodId,
              billingFullName: billingInfo[BillingInfoFieldName.FullName] || '',
              billingCompanyName: billingInfo[BillingInfoFieldName.Company] || '',
              billingAddress,
            });
          }
        }
      } else {
        onChange({
          ...formData,
          paymentMethodId,
        });
      }

      setTouched((prev) => ({
        ...prev,
        [PAYMENT_METHOD_ID_FIELD]: true,
      }));
    },
    [formData, onChange, sellerCountry, setTouched, paymentMethodDetails, isDisabled],
  );

  const handlePaymentMethodCreated = useCallback(
    async (paymentMethodId: string, billingInfoFromPaymentSection?: BillingInfoData) => {
      setIsNewPaymentMethodSelected(false);

      // Read from ref so that stale Adyen callbacks always see the latest form state
      const currentFormData = formDataRef.current;

      let updatedFormData: Partial<WwexFormData>;

      // Use billing info from payment section (passed as parameter), fallback to modal state
      const billingInfoToUse = billingInfoFromPaymentSection || fsuiBillingInfo;

      // Use billing info captured from FSUI during card creation
      if (billingInfoToUse) {
        const billingAddress = {
          line1: billingInfoToUse[BillingInfoFieldName.Line1] || '',
          line2: billingInfoToUse[BillingInfoFieldName.Line2] || '',
          line3: '',
          city: billingInfoToUse[BillingInfoFieldName.City] || '',
          state: billingInfoToUse[BillingInfoFieldName.State] || '',
          postalCode: billingInfoToUse[BillingInfoFieldName.PostalCode] || '',
          countryCode: billingInfoToUse[BillingInfoFieldName.CountryCode] || 'US',
        };

        const shippingAddress = { ...billingAddress };

        updatedFormData = {
          ...currentFormData,
          paymentMethodId,
          billingFullName: billingInfoToUse[BillingInfoFieldName.FullName] || '',
          billingCompanyName: billingInfoToUse[BillingInfoFieldName.Company] || '',
          billingAddress,
          shippingAddress,
        };
      } else {
        updatedFormData = {
          ...currentFormData,
          paymentMethodId,
        };
      }

      // Update local form state
      onChange(updatedFormData);

      // Notify parent modal with both the payment method ID and the complete updated form data
      // This ensures the modal has all the data it needs without waiting for state propagation
      onPaymentMethodCreated?.(paymentMethodId, updatedFormData, billingInfoToUse);
    },
    [fsuiBillingInfo, onChange, onPaymentMethodCreated],
  );

  // ===================
  // Auto-pay & Terms Handlers
  // ===================

  const handleAutoPayChange = useCallback(
    (accepted: boolean) => {
      if (isDisabled) return;
      onChange({
        ...formData,
        autoPayAccepted: accepted,
      });
      // Don't set touched immediately - prevents validation flash during state update
      // Field will show errors via showAllErrors on submit attempt
    },
    [formData, onChange, isDisabled],
  );

  const handleTermsChange = useCallback(
    (accepted: boolean) => {
      if (isDisabled) return;
      onChange({
        ...formData,
        termsAccepted: accepted,
      });
      // Don't set touched immediately - prevents validation flash during state update
      // Field will show errors via showAllErrors on submit attempt
    },
    [formData, onChange, isDisabled],
  );

  // ===================
  // Render
  // ===================

  return (
    <FlexColumn gap={Gap.Space16}>
      {/* User Information Section */}
      <FlexColumn gap={Gap.Space12}>
        <Text textType={TextType.H4} value={messages.userInfoSectionTitle} />
        <UserInfoForm
          firstName={formData.firstName ?? ''}
          lastName={formData.lastName ?? ''}
          companyName={formData.companyName ?? ''}
          email={formData.email ?? ''}
          phoneNumber={formData.phoneNumber ?? ''}
          errors={errors as Record<string, ValidationError>}
          showAllErrors={showAllErrors}
          isDisabled={isDisabled}
          onChange={handleUserInfoChange}
          onBlur={handleUserInfoBlur}
        />
      </FlexColumn>

      {/* Payment Method Selector */}
      <FlexColumn gap={Gap.Space12}>
        <AuctanePayAccountSelect
          value={formData.paymentMethodId ?? ''}
          errorMessage={
            showAllErrors && touched[PAYMENT_METHOD_ID_FIELD]
              ? getErrorMessage((errors as Record<string, ValidationError>)[PAYMENT_METHOD_ID_FIELD])
              : undefined
          }
          showError={
            showAllErrors && touched[PAYMENT_METHOD_ID_FIELD] && !!(errors as Record<string, ValidationError>)[PAYMENT_METHOD_ID_FIELD]
          }
          disabled={isDisabled}
          onChange={handlePaymentMethodChange}
        />
      </FlexColumn>

      {/* Add Payment Method Section */}
      {isNewPaymentMethodSelected && (
        <PaymentMethodSection
          isVisible={isNewPaymentMethodSelected}
          onPaymentMethodCreated={handlePaymentMethodCreated}
          onSubmitFunctionReady={onPaymentSubmitFunctionReady}
        />
      )}

      {/* Billing Address Section - Show for existing payment methods (data goes to WWEX, not payment method update) */}
      {shouldShowBillingAddress && (
        <FlexColumn gap={Gap.Space12}>
          <Text textType={TextType.H4} value={messages.billingAddressSectionTitle} />
          <BillingAddressForm
            data={{
              fullName: formData.billingFullName ?? '',
              companyName: formData.billingCompanyName ?? '',
              address: formData.billingAddress ?? { line1: '', city: '', state: '', postalCode: '', countryCode: 'US' },
            }}
            errors={{
              billingFullName: (errors as Record<string, ValidationError>)?.billingFullName,
              billingCompanyName: (errors as Record<string, ValidationError>)?.billingCompanyName,
              ...((errors as Record<string, unknown>)?.billingAddress as Record<string, ValidationError>),
            }}
            showAllErrors={showAllErrors}
            isDisabled={isDisabled}
            onChange={handleBillingAddressChange}
            onBlur={handleBillingAddressBlur}
          />
        </FlexColumn>
      )}

      {/* Auto-Pay Authorization */}
      <AutoPayAcceptanceSection
        isAccepted={formData.autoPayAccepted ?? false}
        isDisabled={isDisabled}
        error={
          showAllErrors || touched[AUTO_PAY_ACCEPTED_FIELD]
            ? (errors as Record<string, ValidationError>)[AUTO_PAY_ACCEPTED_FIELD]
            : undefined
        }
        onAcceptChange={handleAutoPayChange}
      />

      {/* Terms & Conditions */}
      <TermsSection
        isAccepted={formData.termsAccepted ?? false}
        isDisabled={isDisabled}
        error={
          showAllErrors || touched[TERMS_ACCEPTED_FIELD] ? (errors as Record<string, ValidationError>)[TERMS_ACCEPTED_FIELD] : undefined
        }
        onAcceptChange={handleTermsChange}
      />
    </FlexColumn>
  );
};
